import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ScanLine, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const SEV = {
  low:      "text-emerald-700 bg-emerald-50",
  medium:   "text-amber-700 bg-amber-50",
  high:     "text-orange-700 bg-orange-50",
  critical: "text-rose-700 bg-rose-50",
};

export default function Scans() {
  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/scans")
      .then((r) => setScans(r.data))
      .catch((err) => toast.error("Couldn't load scans", { description: formatApiError(err) }))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-8" data-testid="scans-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">Scans</p>
          <h1 className="mt-1.5 font-display text-3xl font-semibold tracking-tight text-slate-900">Scan history</h1>
          <p className="mt-1.5 text-sm text-slate-600">Every AI compliance scan run in this workspace.</p>
        </div>
        <Button onClick={() => navigate("/documents")} className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white">
          <ScanLine className="h-4 w-4 mr-2" /> New scan
        </Button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.12em] text-slate-500 font-semibold">
            <tr>
              <th className="px-5 py-3">Document</th>
              <th className="px-5 py-3">Score</th>
              <th className="px-5 py-3">Level</th>
              <th className="px-5 py-3">Issues</th>
              <th className="px-5 py-3">Run</th>
              <th className="px-5 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" /> Loading…
              </td></tr>
            )}
            {!loading && scans.length === 0 && (
              <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-500">
                No scans yet. <Link to="/documents" className="text-blue-600 hover:text-blue-700 font-medium">Upload a document</Link> to run your first one.
              </td></tr>
            )}
            {scans.map((s) => (
              <tr key={s.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/scans/${s.id}`)} data-testid={`scan-row-${s.id}`}>
                <td className="px-5 py-3 font-medium text-slate-900">{s.document_name || "Ad-hoc clause"}</td>
                <td className="px-5 py-3 font-mono">
                  {s.status === "queued" ? <span className="text-slate-400">—</span>
                    : s.status === "failed" ? <span className="text-rose-500">—</span>
                    : s.score}
                </td>
                <td className="px-5 py-3">
                  {s.status === "queued" ? (
                    <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold text-blue-700 bg-blue-50">
                      <Loader2 className="h-3 w-3 animate-spin" /> Analyzing
                    </span>
                  ) : s.status === "failed" ? (
                    <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold text-rose-700 bg-rose-50">failed</span>
                  ) : (
                    <span className={"rounded-full px-2 py-0.5 text-[11px] font-semibold " + (SEV[s.level] || SEV.medium)}>{s.level}</span>
                  )}
                </td>
                <td className="px-5 py-3 text-slate-600">{s.issue_count}</td>
                <td className="px-5 py-3 text-slate-500 text-xs">{new Date(s.created_at).toLocaleString()}</td>
                <td className="px-5 py-3 text-right">
                  <ArrowRight className="h-4 w-4 text-slate-400 inline" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
